import { createApi } from "@reduxjs/toolkit/query/react";
import { MODIFIER_OPTION, TagId, TagType } from "../../configs";
import {
  HttpMethod,
  IWriteModifierOption,
  ModifierOption,
  ModifierOptionDetails,
  PaginatedResponse,
  SearchByDisplayName,
} from "../../types";
import { createBaseQuery } from "../helpers/baseQueryHelper";

const modifierOptionApi = createApi({
  reducerPath: MODIFIER_OPTION.reducerPath,
  baseQuery: createBaseQuery(MODIFIER_OPTION.baseUrl),
  tagTypes: MODIFIER_OPTION.tagTypes,
  endpoints(builder) {
    return {
      fetchModifierOptions: builder.query<
        PaginatedResponse<ModifierOption>,
        { restaurantId: string; searchQuery: SearchByDisplayName }
      >({
        query: ({ restaurantId, searchQuery }) => {
          return {
            url: `/${restaurantId}/restaurant`,
            method: HttpMethod.GET,
            params: searchQuery,
          };
        },
        providesTags: () => [{ type: TagType.ModifierOption, id: TagId.LIST }],
      }),

      fetchAllModifierOptions: builder.query<ModifierOption[], string>({
        query: (restaurantId) => {
          return {
            url: `/${restaurantId}/restaurant/all`,
            method: HttpMethod.GET,
          };
        },
        providesTags: () => [
          { type: TagType.ModifierOption, id: TagId.ALL_LIST },
        ],
      }),

      toggleModifierOptionStock: builder.mutation<
        void,
        { modifierOptionId: string; haveStock: boolean }
      >({
        query: ({ modifierOptionId, haveStock }) => ({
          url: `/${modifierOptionId}/stock`,
          method: HttpMethod.PATCH,
          params: { haveStock },
        }),
        invalidatesTags: () => [
          { type: TagType.ModifierOption, id: TagId.LIST },
        ],
      }),

      createModifierOption: builder.mutation<
        ModifierOptionDetails,
        IWriteModifierOption
      >({
        query: (body) => ({
          url: "",
          method: HttpMethod.POST,
          body,
        }),
        invalidatesTags: () => [
          { type: TagType.ModifierOption, id: TagId.ALL_LIST },
          { type: TagType.ModifierOption, id: TagId.LIST },
        ],
      }),

      updateModifierOption: builder.mutation<
        void,
        { modifierOptionId: string; body: IWriteModifierOption }
      >({
        query: ({ modifierOptionId, body }) => ({
          url: `/${modifierOptionId}`,
          method: HttpMethod.PUT,
          body,
        }),
        invalidatesTags: (_result, _error, { modifierOptionId }) => [
          { type: TagType.ModifierOption, id: TagId.ALL_LIST },
          { type: TagType.ModifierOption, id: TagId.LIST },
          { type: TagType.ModifierOption, id: modifierOptionId },
        ],
      }),

      fetchModifierOptionDetails: builder.query<ModifierOptionDetails, string>({
        query: (restaurantId) => {
          return {
            url: `/${restaurantId}`,
            method: HttpMethod.GET,
          };
        },
        providesTags: (_result, _error, modifierOptionId) => [
          { type: TagType.ModifierOption, id: modifierOptionId },
        ],
      }),

      deleteModifierOption: builder.mutation<void, string>({
        query: (modifierOptionId) => ({
          url: `/${modifierOptionId}`,
          method: HttpMethod.DELETE,
        }),
        invalidatesTags: (_result, _error, modifierOptionId) => [
          { type: TagType.ModifierOption, id: TagId.ALL_LIST },
          { type: TagType.ModifierOption, id: TagId.LIST },
          { type: TagType.ModifierOption, id: modifierOptionId },
        ],
      }),
    };
  },
});

export const {
  useFetchModifierOptionsQuery,
  useToggleModifierOptionStockMutation,
  useCreateModifierOptionMutation,
  useUpdateModifierOptionMutation,
  useFetchModifierOptionDetailsQuery,
  useDeleteModifierOptionMutation,
  useFetchAllModifierOptionsQuery,
} = modifierOptionApi;

export { modifierOptionApi };
